import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../../core/auth/auth.service';
import { environment } from '../../../../environments/environment';

export interface RecentEntry {
  kind: 'customer' | 'useraccount' | 'phoneservice';
  entityId: string;
  customerId: string;
  name: string;
  brand: string;
  at: number; // epoch ms of last open
}

const RECENT_KINDS: readonly RecentEntry['kind'][] = ['customer', 'useraccount', 'phoneservice'];

/** Minimal shape guard for a persisted entry — rejects stale/tampered records missing rendered fields. */
function isRecentEntry(value: unknown): value is RecentEntry {
  if (!value || typeof value !== 'object') return false;
  const e = value as Partial<RecentEntry>;
  return (
    typeof e.kind === 'string' && (RECENT_KINDS as string[]).includes(e.kind) &&
    typeof e.entityId === 'string' &&
    typeof e.customerId === 'string' &&
    typeof e.name === 'string' &&
    typeof e.brand === 'string'
  );
}

/** Parse a JSON string into a RecentEntry, returning null on failure. */
function parseRecentString(s: string): RecentEntry | null {
  try {
    const parsed = JSON.parse(s);
    return isRecentEntry(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Backend profile DTO shape (GET/PUT /internalsearch/v1/search/profile).
 * Recents are stored as JSON strings (each a serialized RecentEntry).
 */
interface UserSearchProfileDTO {
  recents?: string[];
  activeOnly?: boolean;
  sort?: string;
  searchMode?: string;
}

/**
 * Recently opened entities, persisted via the backend profile endpoint (spec §5.9, EB-4074).
 * Falls back to localStorage when the endpoint is unavailable.
 */
@Injectable({ providedIn: 'root' })
export class RecentsService {
  private readonly authService = inject(AuthService);
  private readonly http = inject(HttpClient);
  private static readonly MAX = 8;

  /**
   * In-memory cache — seeded from localStorage on first list(), replaced by the backend
   * profile once its GET lands, written through on add()/clear(). A signal rather than a
   * plain field so views repaint when the async backend load resolves: on a fresh device
   * (empty localStorage, populated backend) a snapshot taken at construction would paint an
   * empty list and never recover.
   */
  private readonly entries = signal<RecentEntry[] | null>(null);

  /**
   * True once add()/clear() has written locally. Guards against the lost-update race: an
   * in-flight profile GET issued before the write must not overwrite the newer local list
   * with its older snapshot.
   */
  private locallyModified = false;
  private profileLoaded = false;

  /** Reactive view of the recents list — repaints when the backend profile load resolves. */
  readonly recents: Signal<RecentEntry[]> = computed(() => this.entries() ?? []);

  private key(): string | null {
    const userId = this.authService.currentUserId;
    return userId ? `amie.recents.${userId}` : null;
  }

  /**
   * Load recents. Seeds synchronously from localStorage and kicks off the backend profile
   * fetch (once per session); the returned array reflects what is known right now, while
   * {@link recents} additionally repaints once the backend answers.
   */
  list(): RecentEntry[] {
    if (this.entries() === null) {
      this.entries.set(this.listFromLocalStorage());
    }

    if (!this.profileLoaded) {
      this.profileLoaded = true;
      this.loadFromBackend();
    }

    return this.entries() ?? [];
  }

  add(entry: RecentEntry): void {
    const key = this.key();
    if (!key) return;

    try {
      const current = this.entries() ?? this.listFromLocalStorage();
      const rest = current.filter((e) => !(e.kind === entry.kind && e.entityId === entry.entityId));
      const next = [{ ...entry, at: Date.now() }, ...rest].slice(0, RecentsService.MAX);
      this.locallyModified = true;
      this.entries.set(next);

      // Write to localStorage (immediate, synchronous)
      localStorage.setItem(key, JSON.stringify(next));

      // Write to backend (fire-and-forget — never blocks navigation)
      this.saveToBackend(next);
    } catch {
      /* quota / unavailable — non-fatal */
    }
  }

  clear(): void {
    const key = this.key();
    if (!key) return;
    try {
      this.locallyModified = true;
      this.entries.set([]);
      localStorage.removeItem(key);
      this.saveToBackend([]);
    } catch {
      /* unavailable — non-fatal */
    }
  }

  private listFromLocalStorage(): RecentEntry[] {
    const key = this.key();
    if (!key) return [];
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? (JSON.parse(raw) as unknown) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isRecentEntry);
    } catch {
      return [];
    }
  }

  /** Load profile from backend and merge into cache. Non-blocking, non-fatal. */
  private loadFromBackend(): void {
    this.http
      .get<UserSearchProfileDTO>(`${environment.searchUrl}/search/profile`)
      .subscribe({
        next: (profile) => {
          /* A local add()/clear() won the race while this GET was in flight. Its snapshot
           * predates that write, so adopting it here would drop the entry the user just
           * opened from the in-memory list for the rest of the session. */
          if (this.locallyModified) return;

          if (profile?.recents?.length) {
            const entries = profile.recents
              .map(parseRecentString)
              .filter((e): e is RecentEntry => e !== null);
            if (entries.length > 0) {
              const loaded = entries.slice(0, RecentsService.MAX);
              this.entries.set(loaded);
              // Sync to localStorage so next session has the data even if backend is slow
              const key = this.key();
              if (key) {
                try {
                  localStorage.setItem(key, JSON.stringify(loaded));
                } catch { /* non-fatal */ }
              }
            }
          }
        },
        error: () => {
          /* Backend unavailable — localStorage fallback is already active */
        },
      });
  }

  /** Persist recents to backend. Fire-and-forget, non-fatal. */
  private saveToBackend(entries: RecentEntry[]): void {
    const recents = entries.map((e) => JSON.stringify(e));
    this.http
      .put<UserSearchProfileDTO>(`${environment.searchUrl}/search/profile`, { recents })
      .subscribe({
        error: () => {
          /* Backend unavailable — localStorage has the data */
        },
      });
  }
}
